import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useAsync } from '../hooks/useAsync'
import { useSession } from '../context/SessionContext'
import { startDiscordLogin } from '../services/auth'
import { errorMessage } from '../services/api'
import {
  fetchGames,
  fetchMySchedule,
  fetchSeason,
  fetchSeasons,
  fetchSignupStatus,
  fetchSnapshot,
  fetchStandings,
  signUpForSeason,
  withdrawFromSeason,
} from '../services/league'
import {
  Avatar,
  Card,
  EmptyState,
  Loading,
  Notice,
  PlayerCell,
  PlayoffBadge,
  ProgressBar,
  Stat,
  formatCalendarDate,
  formatDate,
  formatPct,
  formatRecord,
  formatSeasonStatus,
  formatSharePct,
  playerProfilePath,
} from '../components/ui'
import StandingsTable from '../components/StandingsTable'
import { CopyDiscordTags } from '../components/CopyDiscordTags'
import HomePoll from '../components/HomePoll'
import HomeUpdate from '../components/HomeUpdate'
import { LOGO_SRC } from '../constants/branding'
import type {
  Conference,
  GameSummary,
  P1Record,
  Season,
  SeasonSignupStatus,
  StandingsRow,
} from '../types'
import './Home.css'

/**
 * One conference's seeded table, bye and playoff lines included.
 *
 * @param rows - The conference in seeded order, straight from the API. Filtering
 * the overall list into this would lose the tiebreakers and put the cut lines in
 * the wrong place.
 * @param seasonNumber - Season these rows describe, so a row opens that player's
 * results for it. Rows here are clickable for the same reason they are on the
 * standings page: this renders the same data through the same components, and a
 * table that responds to a click in one place and not the other reads as a bug.
 *
 * @remarks
 * The table itself is {@link StandingsTable}, the same component the standings
 * page uses, so the two cannot disagree about when a playoff line may be drawn.
 */
function ConferencePreview({
  conference,
  rows,
  seasonNumber,
}: {
  conference: Conference
  rows: StandingsRow[]
  seasonNumber?: number
}) {
  return (
    <Card
      title={`${conference} Standings`}
      subtitle={`${rows.length} ${rows.length === 1 ? 'player' : 'players'}`}
      conference={conference}
      actions={
        <Link className="btn btn--ghost btn--sm" to="/standings">
          All seasons
        </Link>
      }
      flush
    >
      {rows.length === 0 ? (
        <EmptyState title={`No ${conference} players yet`}>
          An admin assigns conferences from the Admin page.
        </EmptyState>
      ) : (
        <StandingsTable rows={rows} seeded seasonNumber={seasonNumber} />
      )}
    </Card>
  )
}

/**
 * The season's deadlines, sat beside its lifecycle badge.
 *
 * @param season - The current season, or undefined while it loads.
 *
 * @remarks
 * A deadline the league has not set renders nothing rather than an em dash: a
 * blank "Midseason —" beside ACTIVE reads as a date that exists and is being
 * withheld. Every season before 33 sets neither, so this whole row is absent
 * there and the header looks exactly as it always has.
 *
 * The midseason chip carries its games-played requirement in a tooltip rather
 * than on screen, since the date is the part a player needs at a glance and the
 * header has to survive a phone.
 *
 * A bare date is ambiguous to the members who are not in Central, and by hours
 * that matter on the last night, so the zone is stated once after the chips
 * rather than inside each of them.
 */
function SeasonDeadlines({ season }: { season?: Season }) {
  if (!season) return null

  const deadlines = [
    season.midseasonDeadline && {
      label: 'Midseason',
      date: season.midseasonDeadline,
      hint:
        season.midseasonRequiredGames === null
          ? undefined
          : `${season.midseasonRequiredGames} games must be played by the end of this date, midnight CT`,
    },
    season.endOfSeasonDeadline && {
      label: 'End of season',
      date: season.endOfSeasonDeadline,
      hint: 'Every regular season game must be played by the end of this date, midnight CT',
    },
  ].filter(Boolean) as Array<{ label: string; date: string; hint?: string }>

  if (deadlines.length === 0) return null

  return (
    <>
      {deadlines.map(deadline => (
        <span className="deadline" key={deadline.label} title={deadline.hint}>
          <span className="deadline__label">{deadline.label}</span>
          {formatCalendarDate(deadline.date)}
        </span>
      ))}
      <span
        className="deadline__zone"
        title="Deadlines run to the end of their day in US Central time, so a game played that evening still counts."
      >
        ends midnight CT
      </span>
    </>
  )
}

function RecentGameRow({ game }: { game: GameSummary }) {
  const homeWon = game.home.score > game.away.score
  const awayWon = game.away.score > game.home.score

  return (
    <Link to={`/games/${game.id}`} className="recent-game">
      <div className="recent-game__side">
        <PlayerCell
          name={game.home.displayName}
          discordId={game.home.discordId}
          avatar={game.home.discordAvatar}
          team={game.home.teamAbbreviation}
        />
      </div>
      <div className="recent-game__score numeric">
        <span className={homeWon ? '' : 'muted'}>{game.home.score}</span>
        <span className="recent-game__dash">–</span>
        <span className={awayWon ? '' : 'muted'}>{game.away.score}</span>
        <PlayoffBadge isPlayoff={game.isPlayoff} compact />
      </div>
      <div className="recent-game__side recent-game__side--away">
        <PlayerCell
          name={game.away.displayName}
          discordId={game.away.discordId}
          avatar={game.away.discordAvatar}
          team={game.away.teamAbbreviation}
        />
      </div>
    </Link>
  )
}

/** Everything the hero's signup button and the line under it need. */
interface SignupControl {
  /** Null only while the first load is in flight. */
  season: SeasonSignupStatus | null
  pending: boolean
  notice: { tone: 'success' | 'error'; text: string } | null
  /** Signs a member up, or sends a visitor through Discord to do it. */
  join: () => void
  withdraw: () => void
}

/**
 * Turns the `?signup=` the Discord callback lands on into something to read.
 *
 * @remarks
 * The signed-out path leaves the site entirely and comes back on a fresh page
 * load, so the outcome has to travel in the URL. Only the failure strictly
 * needs saying — a successful signup is already legible from the button — but
 * a member who has just been bounced through Discord deserves to be told the
 * thing they set out to do is done, rather than having to infer it.
 */
function returnNotice(
  outcome: string | null,
  season: SeasonSignupStatus | null
): SignupControl['notice'] {
  if (!season) return null

  if (outcome === 'joined') {
    return { tone: 'success', text: `You're signed up for ${season.name}.` }
  }
  if (outcome === 'failed') {
    return {
      tone: 'error',
      text: `You're signed in, but the ${season.name} signup did not go through. Try the button again.`,
    }
  }
  return null
}

/**
 * Signup state for the season after the current one.
 *
 * @remarks
 * The server's reply to a signup or a withdrawal is the new state, so it is
 * kept and shown rather than triggering a refetch — the button flips the
 * instant the write lands instead of a round trip later.
 */
function useSeasonSignup(): SignupControl {
  const { user } = useSession()
  const [searchParams, setSearchParams] = useSearchParams()
  const loaded = useAsync(fetchSignupStatus, [user?.id])
  const [applied, setApplied] = useState<SeasonSignupStatus | null>(null)
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Captured before the effect below strips it, so the message survives the
  // rewrite but a later refresh does not replay a redirect already handled.
  const [outcome] = useState(() => searchParams.get('signup'))

  useEffect(() => {
    if (!searchParams.has('signup')) return
    const next = new URLSearchParams(searchParams)
    next.delete('signup')
    setSearchParams(next, { replace: true })
  }, [searchParams, setSearchParams])

  useEffect(() => {
    setApplied(null)
  }, [user?.id])

  const season = applied ?? loaded.data

  async function run(action: () => Promise<SeasonSignupStatus>): Promise<void> {
    setPending(true)
    setError(null)

    try {
      setApplied(await action())
    } catch (caught) {
      setError(errorMessage(caught))
    } finally {
      setPending(false)
    }
  }

  return {
    season,
    pending,
    notice: error ? { tone: 'error', text: error } : returnNotice(outcome, season),
    join: () => (user ? void run(signUpForSeason) : startDiscordLogin('season-signup')),
    withdraw: () => void run(withdrawFromSeason),
  }
}

/**
 * The signup button in whichever of its three states applies.
 *
 * @remarks
 * A signed-out visitor gets the same wording as a member, because the sign-in
 * is plumbing rather than the thing they are being asked to do: the click
 * carries its intent through Discord and comes back with the signup made.
 *
 * The signed-up state is a chip and a withdrawal, not a disabled button. A
 * greyed-out "Sign up for Season 34" reads as unavailable, which is the
 * opposite of what it would mean.
 */
function SeasonSignupButton({ control }: { control: SignupControl }) {
  const { season, pending } = control

  if (!season?.open) return null

  if (season.signedUp) {
    return (
      <>
        <span className="signup-chip">
          <span className="signup-chip__mark" aria-hidden="true">
            ✓
          </span>
          You&rsquo;re in for {season.name}
        </span>
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          disabled={pending}
          onClick={control.withdraw}
        >
          {pending ? 'Withdrawing…' : 'Withdraw'}
        </button>
      </>
    )
  }

  return (
    <button
      type="button"
      className="btn btn--accent"
      disabled={pending}
      onClick={control.join}
    >
      {pending ? 'Signing up…' : `Sign up for ${season.name}`}
    </button>
  )
}

/**
 * Hero call-to-action row.
 *
 * @param seasonNumber - Season the schedule and tag list are built from.
 *
 * @remarks
 * Signing up for next season is the one action here that works signed out, so
 * it leads both rows; everything beside it needs a session, which is why the
 * signed-out row swaps to what a visitor can actually use rather than
 * collapsing to a single orphaned button.
 *
 * The signup button keeps the blue accent rather than the red primary so it
 * sits beside the existing calls to action instead of competing with them. It
 * is absent while the current season is still being played — signups are for
 * the gap after that season is complete.
 *
 * The hero links are handed to {@link CopyDiscordTags} as children so all the
 * buttons share one row; the copy button owns its own feedback below it.
 */
function HeroActions({ seasonNumber }: { seasonNumber?: number }) {
  const { user } = useSession()
  const signup = useSeasonSignup()

  const schedule = useAsync(
    () => (user ? fetchMySchedule(seasonNumber) : Promise.resolve(null)),
    [user?.id, seasonNumber]
  )

  const signupNotice = signup.notice && (
    <Notice tone={signup.notice.tone}>{signup.notice.text}</Notice>
  )

  if (!user) {
    return (
      <>
        <div className="row">
          <SeasonSignupButton control={signup} />
          <Link className="btn btn--primary" to="/account">
            Sign in with Discord
          </Link>
          <Link className="btn" to="/standings">
            View Standings
          </Link>
        </div>

        {signupNotice}
      </>
    )
  }

  return (
    <>
      <CopyDiscordTags schedule={schedule.data} loading={schedule.loading}>
        <SeasonSignupButton control={signup} />
        <Link className="btn" to="/upload">
          Upload Game Result
        </Link>
        <Link className="btn btn--primary" to="/my-schedule">
          My Schedule
        </Link>
      </CopyDiscordTags>

      {signupNotice}
      {schedule.error && <Notice tone="error">{schedule.error}</Notice>}
    </>
  )
}

/**
 * Top of a conference table as a stat tile.
 *
 * @param rows - Standings for one conference, already in league rank order.
 *
 * @remarks
 * Before any game is played every row is 0-0, so the first row is only the
 * seeded or alphabetical top rather than a leader. That case renders "TBD"
 * instead of naming somebody who has not earned it.
 */
function ConferenceLeaderStat({
  conference,
  rows,
  loading,
}: {
  conference: Conference
  rows: StandingsRow[]
  loading: boolean
}) {
  const label = `${conference} Leader`
  const accent = conference.toLowerCase() as Lowercase<Conference>

  if (loading) {
    return <Stat label={label} accent={accent} value="—" hint="Loading standings" size="compact" />
  }
  if (rows.length === 0) {
    return (
      <Stat label={label} accent={accent} value="—" hint="No players assigned yet" size="compact" />
    )
  }
  if (!rows.some(row => row.gamesPlayed > 0)) {
    return (
      <Stat label={label} accent={accent} value="TBD" hint="No games played yet" size="compact" />
    )
  }

  const leader = rows[0]
  return (
    <Stat
      label={label}
      accent={accent}
      to={leader.userId === null ? undefined : playerProfilePath(leader.userId)}
      value={
        <span className="stat__leader">
          <Avatar
            name={leader.displayName}
            discordId={leader.discordId}
            avatar={leader.discordAvatar}
          />
          <span>{leader.displayName}</span>
        </span>
      }
      hint={`${formatRecord(leader.wins, leader.losses, leader.ties)} · ${formatPct(
        leader.winPercentage
      )}`}
      size="compact"
    />
  )
}

/**
 * Share of games won by whoever held the player-one controller.
 *
 * @param record - All-time home-slot split from the snapshot, or undefined
 * while it loads.
 *
 * @remarks
 * The percentage alone would hide how thin the sample is, so the hint carries
 * the record and the game count that produced it. A league with no decided
 * games renders an em dash rather than dividing by zero.
 */
function P1RecordStat({ record, loading }: { record?: P1Record; loading: boolean }) {
  const label = 'P1 Total Record'

  if (loading) {
    return <Stat label={label} value="—" hint="Loading results" />
  }
  if (!record || record.winPercentage === null) {
    return <Stat label={label} value="—" hint="No games played yet" />
  }

  return (
    <Stat
      label={label}
      value={`${(record.winPercentage * 100).toFixed(1)}%`}
      hint={`${formatRecord(record.wins, record.losses, 0)} as P1 · ${record.games} ${
        record.games === 1 ? 'game' : 'games'
      }`}
    />
  )
}

export default function Home() {
  const seasons = useAsync(fetchSeasons, [])
  const currentSeason = seasons.data?.find(season => season.isCurrent)
  const seasonNumber = currentSeason?.number

  const standings = useAsync(
    () => fetchStandings(seasonNumber),
    [seasonNumber]
  )
  const snapshot = useAsync(() => fetchSnapshot(seasonNumber), [seasonNumber])
  const recent = useAsync(
    () => fetchGames({ season: seasonNumber, status: 'official', limit: 6 }),
    [seasonNumber]
  )
  const seasonDetail = useAsync(
    () => (seasonNumber ? fetchSeason(seasonNumber) : Promise.resolve(null)),
    [seasonNumber]
  )

  // Intra-conference round robin: the league target is the sum of N-choose-2
  // per conference, which the season endpoint already derives.
  const scheduledGames =
    seasonDetail.data?.schedule.reduce((total, row) => total + row.scheduledGames, 0) ?? 0
  // Both the bar and the headline count regular-season games, since the target
  // beside them is a round robin. Postseason games are named separately.
  const playedGames = snapshot.data?.totalGames ?? 0
  const playoffGames = snapshot.data?.playoffGames ?? 0

  return (
    <div className="stack">
      <section className="hero">
        <div className="hero__content">
          <div className="hero__masthead">
            <div className="hero__titles">
              <div className="hero__status">
                <span className="eyebrow">
                  {currentSeason
                    ? `Season ${currentSeason.number} · ${formatSeasonStatus(currentSeason.status)}`
                    : 'Loading season'}
                </span>
                <SeasonDeadlines season={currentSeason} />
              </div>
              <h1>
                <span className="hero__accent-red">Random</span> Tecmo{' '}
                <span className="hero__accent-blue">League</span>
              </h1>
            </div>
            {/* Decorative: the logo spells the same name as the h1 beside it. */}
            <img
              className="hero__logo"
              src={LOGO_SRC}
              alt=""
              width={645}
              height={400}
            />
          </div>
          <p className="muted">
            Started by the (in)famous 8-Bit and carried on by DPS Big Turtle Benson Boone
            and Nostradamus. The true best Tecmo Super Bowl League around - designed for
            tournament players to hone their skills with OG ROM matchups
          </p>
          <HeroActions seasonNumber={seasonNumber} />
          <HomeUpdate />
          <HomePoll />
        </div>
      </section>

      <div className="grid-3">
        <Stat
          label="Games played"
          value={snapshot.data?.totalGames ?? '—'}
          hint={
            currentSeason
              ? `Season ${currentSeason.number}${
                  playoffGames > 0 ? ` · plus ${playoffGames} playoff` : ''
                }`
              : undefined
          }
          footer={
            <ProgressBar
              value={playedGames}
              total={scheduledGames}
              label={`${playedGames} of ${scheduledGames} scheduled · ${formatSharePct(
                scheduledGames === 0 ? null : playedGames / scheduledGames
              )}`}
            />
          }
        />
        <P1RecordStat record={snapshot.data?.p1Record} loading={snapshot.loading} />
        <ConferenceLeaderStat
          conference="AFC"
          rows={standings.data?.afc ?? []}
          loading={standings.loading}
        />
        <ConferenceLeaderStat
          conference="NFC"
          rows={standings.data?.nfc ?? []}
          loading={standings.loading}
        />
      </div>

      {standings.loading ? (
        <Card title="Standings">
          <Loading />
        </Card>
      ) : (
        <>
          <ConferencePreview
            conference="AFC"
            rows={standings.data?.afc ?? []}
            seasonNumber={seasonNumber}
          />
          <ConferencePreview
            conference="NFC"
            rows={standings.data?.nfc ?? []}
            seasonNumber={seasonNumber}
          />
        </>
      )}

      <Card
        title="Recent results"
        actions={
          <Link className="btn btn--ghost btn--sm" to="/games">
            All games
          </Link>
        }
        flush
      >
        {recent.loading ? (
          <div style={{ padding: 24 }}>
            <Loading rows={3} />
          </div>
        ) : recent.data && recent.data.length > 0 ? (
          <div className="recent-list">
            {recent.data.map(game => (
              <RecentGameRow key={game.id} game={game} />
            ))}
          </div>
        ) : (
          <EmptyState title="No results yet this season">
            The first save state upload will show up here.
          </EmptyState>
        )}
      </Card>

      {recent.data && recent.data.length > 0 && (
        <p className="muted" style={{ fontSize: '0.82rem' }}>
          Last result recorded {formatDate(recent.data[0].playedAt ?? recent.data[0].uploadedAt)}.
        </p>
      )}
    </div>
  )
}
